import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { Loader2, FileText, Users, Clock, CheckCircle, AlertCircle, DollarSign, MessageCircleWarning, Volume2, VolumeX, MessageSquare, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.value = 0.3;
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc.stop(ctx.currentTime + 0.5);
    // Second tone
    setTimeout(() => {
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.frequency.value = 1100;
      osc2.type = 'sine';
      gain2.gain.value = 0.3;
      osc2.start();
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc2.stop(ctx.currentTime + 0.5);
    }, 200);
  } catch {}
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
};

const TicketChatsPanel = ({ token, soundEnabled }) => {
  const [chats, setChats] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [closingId, setClosingId] = useState(null);
  const prevUnread = useRef(-1);

  const fetchChats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ticket-chats`, { headers: { Authorization: `Bearer ${token}` } });
      setChats(res.data.chats || []);
      const unread = res.data.total_unread || 0;
      if (prevUnread.current >= 0 && unread > prevUnread.current && soundEnabled) {
        playNotificationSound();
        toast.info('Nuevo mensaje en un ticket', { duration: 6000 });
      }
      prevUnread.current = unread;
      setTotalUnread(unread);
    } catch { /* ignore */ }
  }, [token, soundEnabled]);

  const closeTicket = useCallback(async (e, ticketId, ticketNumber) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`¿Cerrar el ticket ${ticketNumber}? Dejará de aparecer en este panel.`)) return;
    setClosingId(ticketId);
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/status`, { status: 'closed' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Ticket ${ticketNumber} cerrado`);
      setChats(prev => prev.filter(c => c.ticket_id !== ticketId));
      fetchChats();
    } catch {
      toast.error('No se pudo cerrar el ticket');
    } finally {
      setClosingId(null);
    }
  }, [token, fetchChats]);

  useEffect(() => {
    fetchChats();
    const interval = setInterval(fetchChats, 15000);
    return () => clearInterval(interval);
  }, [fetchChats]);

  if (chats.length === 0) return null;

  return (
    <div className="mt-8" data-testid="ticket-chats-panel">
      <div className="flex items-center gap-3 mb-4">
        <MessageSquare className="w-5 h-5 text-primary" />
        <h2 className="font-heading text-lg font-bold text-white">CHATS DE TICKETS</h2>
        {totalUnread > 0 && (
          <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="chats-unread-badge">
            {totalUnread} sin leer
          </span>
        )}
      </div>
      <div className="bg-surface border border-white/10 rounded-md divide-y divide-white/5 overflow-hidden">
        {chats.slice(0, 15).map(c => (
          <div key={c.ticket_id}
            className={`flex items-center gap-4 px-4 py-3 hover:bg-white/5 transition-colors ${c.unread > 0 ? 'bg-red-500/5' : ''}`}
            data-testid={`chat-row-${c.ticket_number}`}>
            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${c.unread > 0 ? 'bg-red-500 animate-pulse' : 'bg-zinc-700'}`} />
            <Link to={`/admin/tickets/${c.ticket_id}`} className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white text-sm font-medium font-mono">{c.ticket_number}</span>
                <span className="text-zinc-500 text-xs truncate">{c.user_company}{c.vehicle ? ` · ${c.vehicle}` : ''}</span>
              </div>
              <p className={`text-xs truncate mt-0.5 ${c.unread > 0 ? 'text-white font-medium' : 'text-zinc-500'}`}>
                <span className={c.last_message.is_admin ? 'text-primary' : 'text-amber-400'}>
                  {c.last_message.is_admin ? 'Tú' : c.last_message.sender_name}:
                </span>{' '}
                {c.last_message.has_attachment && '📎 '}{c.last_message.text || '(adjunto)'}
              </p>
            </Link>
            <div className="text-right shrink-0">
              <p className="text-zinc-600 text-xs">{timeAgo(c.last_message.created_at)}</p>
              {c.unread > 0 && (
                <span className="inline-block mt-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {c.unread}
                </span>
              )}
            </div>
            <button
              onClick={(e) => closeTicket(e, c.ticket_id, c.ticket_number)}
              disabled={closingId === c.ticket_id}
              title="Cerrar ticket"
              data-testid={`chat-close-${c.ticket_number}`}
              className="shrink-0 flex items-center gap-1 text-xs font-medium text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 border border-white/10 hover:border-emerald-500/30 rounded px-2 py-1.5 transition-colors disabled:opacity-50">
              {closingId === c.ticket_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">Cerrar</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [closingAll, setClosingAll] = useState(false);
  const prevWaiting = useRef(0);

  const closeAllTickets = useCallback(async () => {
    const open = stats?.total_tickets != null
      ? (stats.pending_review || 0) + (stats.needs_info || 0) + (stats.in_progress || 0) + (stats.delivered || 0)
      : 0;
    if (!window.confirm(`¿Cerrar TODOS los tickets abiertos de golpe?\n\nSe marcarán como "Cerrada" sin avisar a los clientes (sin SMS ni email). Podrás reabrirlos después. Esta acción afecta a muchos tickets.`)) return;
    setClosingAll(true);
    try {
      const res = await axios.post(`${API}/admin/tickets/close-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.closed} tickets cerrados`);
      fetchStats(true);
    } catch {
      toast.error('No se pudieron cerrar los tickets');
    } finally {
      setClosingAll(false);
    }
  }, [token, stats]);

  const fetchStats = useCallback(async (silent = false) => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
      const newStats = res.data;

      // Check for new client_waiting tickets
      if (!silent && soundEnabled && newStats.client_waiting > prevWaiting.current && prevWaiting.current >= 0) {
        playNotificationSound();
        toast.warning(`⚠ ${newStats.client_waiting} ticket(s) esperando tu respuesta`, { duration: 8000 });
      }
      prevWaiting.current = newStats.client_waiting || 0;
      setStats(newStats);
    } catch {}
    finally { if (!silent) setLoading(false); }
  }, [token, soundEnabled]);

  useEffect(() => {
    fetchStats(false);
    prevWaiting.current = -1; // Skip first notification
  }, []);

  // Poll every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchStats(true), 15000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div></Layout>;

  const cards = [
    { label: 'Esperando Respuesta', value: stats?.client_waiting || 0, icon: MessageCircleWarning, color: 'text-red-500', bgColor: (stats?.client_waiting || 0) > 0 ? 'bg-red-500/10 border-red-500/30 animate-pulse' : '', link: '/admin/tickets?waiting=true' },
    { label: 'Pendientes', value: stats?.pending_review || 0, icon: Clock, color: 'text-yellow-500', link: '/admin/tickets?status=pending_review' },
    { label: 'Falta Info', value: stats?.needs_info || 0, icon: AlertCircle, color: 'text-orange-500', link: '/admin/tickets?status=needs_info' },
    { label: 'En Proceso', value: stats?.in_progress || 0, icon: Loader2, color: 'text-purple-500', link: '/admin/tickets?status=in_progress' },
    { label: 'Entregadas', value: stats?.delivered || 0, icon: CheckCircle, color: 'text-green-500', link: '/admin/tickets?status=delivered' },
    { label: 'Total Tickets', value: stats?.total_tickets || 0, icon: FileText, color: 'text-blue-500', link: '/admin/tickets' },
    { label: 'Usuarios', value: stats?.users || 0, icon: Users, color: 'text-zinc-400', link: '/admin/users' },
    { label: 'Ingresos', value: `${stats?.revenue || 0}€`, icon: DollarSign, color: 'text-primary' },
  ];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="admin-dashboard">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-3xl font-bold text-white">PANEL ADMIN</h1>
          <div className="flex items-center gap-2">
            <button onClick={closeAllTickets} disabled={closingAll}
              data-testid="close-all-tickets-btn"
              className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors disabled:opacity-50">
              {closingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Cerrar todos
            </button>
            <button onClick={() => setSoundEnabled(!soundEnabled)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs ${soundEnabled ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-zinc-800 text-zinc-500 border border-white/10'}`}>
              {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              Sonido {soundEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map(c => (
            <Link key={c.label} to={c.link || '#'}
              className={`bg-surface border border-white/10 rounded-md p-4 hover:bg-white/5 transition-colors ${c.bgColor || ''}`}>
              <div className="flex items-center gap-3">
                <c.icon className={`w-8 h-8 ${c.color}`} />
                <div>
                  <p className="text-zinc-400 text-sm">{c.label}</p>
                  <p className="text-2xl font-mono font-bold text-white">{c.value}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <TicketChatsPanel token={token} soundEnabled={soundEnabled} />
      </div>
    </Layout>
  );
};

export default AdminDashboard;
